import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { EASE } from "@/lib/motion";

const SLIDES = [
  {
    img: "https://images.pexels.com/photos/38714663/pexels-photo-38714663.jpeg?auto=compress&cs=tinysrgb&w=1600",
    category: "Hair",
    title: "Glass-glossy blowouts, last minute",
  },
  {
    img: "https://images.pexels.com/photos/21412169/pexels-photo-21412169/free-photo-of-close-up-of-an-eye-with-glued-eyelashes.jpeg?auto=compress&cs=tinysrgb&w=1600",
    category: "Brows & Lashes",
    title: "Full, fluttering lash sets",
  },
  {
    img: "https://images.pexels.com/photos/3212179/pexels-photo-3212179.jpeg?auto=compress&cs=tinysrgb&w=1600",
    category: "Skin & Injectables",
    title: "The last-minute glow-up",
  },
  {
    img: "https://images.pexels.com/photos/14483472/pexels-photo-14483472.jpeg?auto=compress&cs=tinysrgb&w=1600",
    category: "Makeup",
    title: "Editorial, gold-leaf artistry",
  },
  {
    img: "https://images.pexels.com/photos/7750124/pexels-photo-7750124.jpeg?auto=compress&cs=tinysrgb&w=1600",
    category: "The Salon",
    title: "Step into last-minute luxury",
  },
];

const variants = {
  enter: (dir) => ({ opacity: 0, x: dir > 0 ? 80 : -80 }),
  center: { opacity: 1, x: 0 },
  exit: (dir) => ({ opacity: 0, x: dir > 0 ? -80 : 80 }),
};

export default function Gallery() {
  const [[index, dir], setState] = useState([0, 0]);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  const paginate = useCallback((newDir) => {
    setState(([prev]) => {
      const next = (prev + newDir + SLIDES.length) % SLIDES.length;
      return [next, newDir];
    });
  }, []);

  const goTo = (i) => setState(([prev]) => [i, i > prev ? 1 : -1]);

  useEffect(() => {
    if (paused) return;
    timer.current = setInterval(() => paginate(1), 4500);
    return () => clearInterval(timer.current);
  }, [paused, paginate]);

  const slide = SLIDES[index];

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative z-10 bg-[#F7F5F2] px-6 py-28 md:px-12 md:py-40"
    >
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="overline mb-6">The Look Book</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#2A2724]">
              A glimpse of the <span className="italic text-gold">glow-up.</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="gallery-prev"
              onClick={() => paginate(-1)}
              className="flex h-12 w-12 items-center justify-center rounded-full border border-black/15 text-[#2A2724] transition-colors duration-300 hover:border-gold hover:text-gold"
              aria-label="Previous"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              data-testid="gallery-next"
              onClick={() => paginate(1)}
              className="flex h-12 w-12 items-center justify-center rounded-full border border-black/15 text-[#2A2724] transition-colors duration-300 hover:border-gold hover:text-gold"
              aria-label="Next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div
          className="relative aspect-[4/5] w-full overflow-hidden rounded-3xl border border-black/10 bg-[#2A2724] sm:aspect-[16/10] lg:aspect-[16/9]"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="gallery-stage"
        >
          <AnimatePresence initial={false} custom={dir} mode="popLayout">
            <motion.img
              key={index}
              src={slide.img}
              alt={slide.title}
              custom={dir}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.7, ease: EASE }}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </AnimatePresence>

          {/* Scrim */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />

          {/* Caption */}
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5, ease: EASE }}
              >
                <span className="overline !text-gold">{slide.category}</span>
                <h3 className="mt-2 max-w-xl font-serif text-3xl md:text-4xl lg:text-5xl font-light text-white">
                  {slide.title}
                </h3>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Slide counter */}
          <div className="absolute right-8 top-8 font-serif text-sm text-white/70">
            <span className="text-gold">{String(index + 1).padStart(2, "0")}</span> /{" "}
            {String(SLIDES.length).padStart(2, "0")}
          </div>
        </div>

        {/* Dots */}
        <div className="mt-8 flex items-center justify-center gap-3">
          {SLIDES.map((s, i) => (
            <button
              key={s.category}
              data-testid={`gallery-dot-${i}`}
              onClick={() => goTo(i)}
              aria-label={`Go to ${s.category}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === index ? "w-10 bg-gold" : "w-4 bg-black/15 hover:bg-black/30"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
